import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  Users,
  Download,
  Trash2,
  Eye,
  UserPlus,
  Mail,
  Phone,
  BookOpen,
  Trophy,
  UserX,
} from "lucide-react";
import {
  getStudents,
  getStudentStats,
  deleteStudent,
  suspendStudent,
} from "../../services/students";

import { StudentWithUserInfo } from "../../types/student";
import StudentModal from "../../components/forms/StudentModal";
import StudentDetailsModal from "../../components/modals/StudentDetailsModal";
import Input from "../../components/common/Input";
import StatsCard from "../../components/common/StatsCard";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import Pagination from "../../components/common/Pagination";

const AdminECourseStudents: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [activeSearchTerm, setActiveSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    | "all"
    | "active"
    | "inactive"
    | "suspended"
    | "graduated"
    | "transferred"
    | "dropped"
  >("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Modal states
  const [isStudentModalOpen, setIsStudentModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(
    null,
  );

  // Handle Enter key press to trigger search
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setActiveSearchTerm(searchTerm);
      setCurrentPage(1);
    }
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  // Fetch e-course students
  const {
    data: studentsResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      "admin-ecourse-students",
      currentPage,
      pageSize,
      activeSearchTerm,
      statusFilter,
    ],
    queryFn: () =>
      getStudents({
        page: currentPage,
        limit: pageSize,
        search: activeSearchTerm || undefined,
        status: statusFilter === "all" ? undefined : statusFilter,
        includeEcourse: "true", // Include e-course students
      }),
  });

  // Fetch e-course student statistics
  const { data: statsResponse } = useQuery({
    queryKey: ["admin-ecourse-student-stats"],
    queryFn: () => getStudentStats(),
    enabled: true,
  });

  const students: StudentWithUserInfo[] = studentsResponse?.data || [];
  const pagination = studentsResponse?.pagination;
  const stats = statsResponse?.data;
  const totalPages = pagination?.totalPages || 1;
  const totalStudents = pagination?.totalItems || 0;

  // Students are already filtered by backend to be e-course students
  const ecourseStudents = students;

  const deleteMutation = useMutation({
    mutationFn: deleteStudent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-ecourse-students"] });
      queryClient.invalidateQueries({
        queryKey: ["admin-ecourse-student-stats"],
      });
      toast.success("E-course student deleted successfully");
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Failed to delete student");
    },
  });

  const suspendMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      suspendStudent(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-ecourse-students"] });
      queryClient.invalidateQueries({
        queryKey: ["admin-ecourse-student-stats"],
      });
      toast.success("E-course student suspended successfully");
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Failed to suspend student");
    },
  });

  const handleDeleteStudent = (studentId: string) => {
    if (
      window.confirm("Are you sure you want to delete this e-course student?")
    ) {
      deleteMutation.mutate(studentId);
    }
  };

  const handleViewDetails = (studentId: string) => {
    setSelectedStudentId(studentId);
    setIsDetailsModalOpen(true);
  };

  const handleAddStudent = () => {
    setSelectedStudentId(null);
    setIsStudentModalOpen(true);
  };

  const exportStudents = () => {
    // TODO: Implement export functionality
    toast.success("Export functionality coming soon!");
  };

  const handleSuspendStudent = (studentId: string) => {
    const reason = prompt("Enter reason for suspension (optional):");
    if (reason === null) return; // User cancelled

    if (
      window.confirm(
        "Are you sure you want to suspend this e-course student? They will not be able to access the platform.",
      )
    ) {
      suspendMutation.mutate({ id: studentId, reason: reason || undefined });
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
        <div className="bg-white/95 backdrop-blur-xl border border-red-200/60 rounded-2xl p-6 shadow-lg">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-pink-500 flex items-center justify-center flex-shrink-0">
              <Trash2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Error Loading E-Course Students
              </h3>
              <p className="text-gray-600 mt-1">
                Unable to load e-course student data. Please try refreshing the
                page.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
            E-Course Students
          </h1>
          <p className="text-slate-600 mt-2 text-sm sm:text-base">
            Manage and track e-course student progress and statistics
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={exportStudents}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300 font-semibold"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        <StatsCard
          title="Total E-Course Students"
          value={totalStudents}
          icon={Users}
          color="blue"
        />
        <StatsCard
          title="Active Students"
          value={
            ecourseStudents.filter((s) => s.academicStatus === "active").length
          }
          icon={UserPlus}
          color="green"
        />
        <StatsCard
          title="Courses Enrolled"
          value={ecourseStudents.reduce(
            (total, student) => total + (student.courses?.length || 0),
            0,
          )}
          icon={BookOpen}
          color="purple"
        />
        <StatsCard
          title="Completion Rate"
          value="0%" // TODO: Calculate from actual progress data
          icon={Trophy}
          color="yellow"
        />
      </div>

      {/* Filters and Search */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl p-4 lg:p-6 shadow-lg border border-slate-200/60">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Search e-course students... (Press Enter)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              className="w-full"
            />
          </div>
          <div className="flex gap-3">
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as any);
                setCurrentPage(1);
              }}
              className="px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
              <option value="graduated">Graduated</option>
              <option value="transferred">Transferred</option>
              <option value="dropped">Dropped</option>
            </select>
          </div>
        </div>
      </div>

      {/* Students Table */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg border border-slate-200/60 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-slate-50 to-blue-50/50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Student
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Enrollment
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Progress
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {ecourseStudents.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center space-y-3">
                      <Users className="w-12 h-12 text-slate-400" />
                      <div className="text-slate-500">
                        <p className="text-lg font-medium">
                          No E-Course Students Found
                        </p>
                        <p className="text-sm">
                          E-course students will appear here once they register
                          through the e-learning platform.
                        </p>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                ecourseStudents.map((student: StudentWithUserInfo) => (
                  <tr
                    key={student._id}
                    className="hover:bg-slate-50/50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-400 to-indigo-400 flex items-center justify-center text-white font-semibold">
                          {student.userInfo?.firstName?.[0]}
                          {student.userInfo?.lastName?.[0]}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-semibold text-slate-900">
                            {student.userInfo?.firstName}{" "}
                            {student.userInfo?.lastName}
                          </div>
                          <div className="text-sm text-slate-500">
                            ID: {student.studentId}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-slate-900">
                        {student.courses?.length || 0} courses
                      </div>
                      <div className="text-sm text-slate-500">
                        Joined{" "}
                        {new Date(student.createdAt).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className="w-16 bg-slate-200 rounded-full h-2">
                          <div
                            className="bg-gradient-to-r from-blue-500 to-indigo-500 h-2 rounded-full"
                            style={{ width: `0%` }}
                          ></div>
                        </div>
                        <span className="text-sm font-semibold text-slate-700">
                          0%
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          student.academicStatus === "enrolled"
                            ? "bg-green-100 text-green-800"
                            : student.academicStatus === "inactive"
                              ? "bg-red-100 text-red-800"
                              : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {student.academicStatus}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleViewDetails(student._id)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded-lg hover:bg-blue-50 transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleSuspendStudent(student._id)}
                          className="text-orange-600 hover:text-orange-900 p-1 rounded-lg hover:bg-orange-50 transition-colors"
                          title="Suspend Student"
                        >
                          <UserX className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="px-6 py-4 bg-slate-50 border-t border-slate-200">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              pageSize={pageSize}
              onPageSizeChange={handlePageSizeChange}
            />
          </div>
        )}
      </div>

      {/* Modals */}
      <StudentModal
        isOpen={isStudentModalOpen}
        onClose={() => setIsStudentModalOpen(false)}
        studentId={selectedStudentId}
      />

      <StudentDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        studentId={selectedStudentId}
      />
    </div>
  );
};

export default AdminECourseStudents;
